/**
 * 最小 ZIP ライター (B5、Phase 6)。
 *
 * STORE 方式 (無圧縮) のみ。OoKey の export は小さな JSON/CSV のため圧縮は不要で、
 * 外部依存 (fflate / jszip 等) を持たずに self-contained で完結させる
 * (Docker named-volume の node_modules 同期問題を避けるため)。
 *
 * 仕様: PKWARE APPNOTE — Local File Header + Central Directory + EOCD。
 */

const CRC_TABLE: readonly number[] = (() => {
  const table: number[] = []
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xed_b8_83_20 ^ (c >>> 1) : c >>> 1
    }
    table[n] = c >>> 0
  }

  return table
})()

const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xff_ff_ff_ff
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8)
  }

  return (crc ^ 0xff_ff_ff_ff) >>> 0
}

/**
 * `files` (ファイル名 → bytes) を 1 つの ZIP (STORE) bytes にまとめる。
 * 添付ファイルが将来加わったときも同じ I/F で同梱できる。
 */
export const zipStore = (files: Readonly<Record<string, Uint8Array>>): Uint8Array => {
  const encoder = new TextEncoder()

  const entries = Object.entries(files).map(([name, data]) => ({
    nameBytes: encoder.encode(name),
    data,
    crc: crc32(data),
  }))

  // DOS time/date は固定 (1980-01-01 00:00) — 再現性のため。
  const dosTime = 0
  const dosDate = 0x00_21

  const localParts: Uint8Array[] = []
  const centralParts: Uint8Array[] = []
  let offset = 0

  const u16 = (v: number): Uint8Array => new Uint8Array([v & 0xff, (v >>> 8) & 0xff])

  const u32 = (v: number): Uint8Array =>
    new Uint8Array([v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff])

  for (const e of entries) {
    const size = e.data.length

    // ── Local file header ──
    const local = [
      u32(0x04_03_4b_50), // signature
      u16(20), // version needed
      u16(0), // flags
      u16(0), // method = STORE
      u16(dosTime),
      u16(dosDate),
      u32(e.crc),
      u32(size), // compressed size
      u32(size), // uncompressed size
      u16(e.nameBytes.length),
      u16(0), // extra length
      e.nameBytes,
      e.data,
    ]

    for (const p of local) localParts.push(p)

    // ── Central directory header ──
    const central = [
      u32(0x02_01_4b_50), // signature
      u16(20), // version made by
      u16(20), // version needed
      u16(0), // flags
      u16(0), // method
      u16(dosTime),
      u16(dosDate),
      u32(e.crc),
      u32(size),
      u32(size),
      u16(e.nameBytes.length),
      u16(0), // extra length
      u16(0), // comment length
      u16(0), // disk number
      u16(0), // internal attrs
      u32(0), // external attrs
      u32(offset), // local header offset
      e.nameBytes,
    ]

    for (const p of central) centralParts.push(p)

    offset += 30 + e.nameBytes.length + size
  }

  const centralSize = centralParts.reduce((sum, p) => sum + p.length, 0)

  // ── End of central directory ──
  const eocd = [
    u32(0x06_05_4b_50),
    u16(0), // disk number
    u16(0), // disk with CD
    u16(entries.length), // entries on this disk
    u16(entries.length), // total entries
    u32(centralSize),
    u32(offset), // CD offset
    u16(0), // comment length
  ]

  const all = [...localParts, ...centralParts, ...eocd]
  const total = all.reduce((sum, p) => sum + p.length, 0)
  const out = new Uint8Array(total)
  let pos = 0
  for (const p of all) {
    out.set(p, pos)
    pos += p.length
  }

  return out
}

/** 文字列を UTF-8 bytes に変換する (zipStore の引数組み立て用)。 */
export const strToBytes = (s: string): Uint8Array => new TextEncoder().encode(s)
